import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE } from '../Constants';
import { CustomModel, CustomModelsResponse, Personality, PersonalitiesResponse } from '../interfaces/CustomModel';
import { safeStorage } from '../../utils/safeStorage';

const ACTIVE_MODEL_STORAGE_KEY = 'maritalk_active_custom_model_id';

export interface CustomModelsContextValue {
  // State
  customModels: CustomModel[];
  predefinedPersonalities: Personality[];
  loading: boolean;
  error: string | null;
  activeModelId: string | null;
  activeModel: CustomModel | Personality | null;

  // Actions
  fetchPersonalities: () => Promise<void>;
  saveModels: (models: CustomModel[]) => Promise<void>;
  addModel: (model: CustomModel) => Promise<void>;
  updateModel: (model: CustomModel) => Promise<void>;
  deleteModel: (modelId: string) => Promise<void>;
  setActiveModelId: (modelId: string | null) => void;
  clearActiveModel: () => void;
  getActiveSystemPrompt: () => string | null;
  getActiveModelName: () => string | null;
}

const CustomModelsContext = createContext<CustomModelsContextValue | null>(null);

export const useCustomModels = (): CustomModelsContextValue => {
  const context = useContext(CustomModelsContext);
  if (!context) {
    throw new Error('useCustomModels must be used within a CustomModelsProvider');
  }
  return context;
};

interface CustomModelsProviderProps {
  children: React.ReactNode;
}

export const CustomModelsProvider: React.FC<CustomModelsProviderProps> = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  const [customModels, setCustomModels] = useState<CustomModel[]>([]);
  const [predefinedPersonalities, setPredefinedPersonalities] = useState<Personality[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeModelId, setActiveModelIdState] = useState<string | null>(
    () => safeStorage.getItem(ACTIVE_MODEL_STORAGE_KEY)
  );

  // Computed active model (check both custom and predefined)
  const activeModel = activeModelId
    ? customModels.find(m => m.id === activeModelId)
      || predefinedPersonalities.find(m => m.id === activeModelId)
      || null
    : null;

  // Persist activeModelId to localStorage
  const setActiveModelId = useCallback((modelId: string | null) => {
    setActiveModelIdState(modelId);
    if (modelId) {
      safeStorage.setItem(ACTIVE_MODEL_STORAGE_KEY, modelId);
    } else {
      safeStorage.removeItem(ACTIVE_MODEL_STORAGE_KEY);
    }
  }, []);

  const clearActiveModel = useCallback(() => {
    setActiveModelId(null);
  }, [setActiveModelId]);

  // Get the active system prompt
  const getActiveSystemPrompt = useCallback((): string | null => {
    if (!activeModel) return null;
    return activeModel.system_prompt;
  }, [activeModel]);

  // Get the model name from the active personality
  const getActiveModelName = useCallback((): string | null => {
    if (!activeModel) return null;
    // Check if it's a Personality (has model field)
    if ('model' in activeModel && activeModel.model) {
      return activeModel.model;
    }
    return null;
  }, [activeModel]);

  // Fetch all personalities from API (requires auth, returns predefined + custom)
  const fetchPersonalities = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}/api/personalities/list`, {
        method: 'GET',
        headers: {
          Authorization: `Auth ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Falha ao carregar personalidades');
      }

      const data: PersonalitiesResponse = await response.json();
      const allPersonalities = data.personalities || [];

      // Separate predefined (id starts with "predefined-") from custom
      const predefined = allPersonalities.filter(p => p.id.startsWith('predefined-'));
      const custom = allPersonalities.filter(p => !p.id.startsWith('predefined-'));

      setPredefinedPersonalities(predefined);
      // Convert Personality to CustomModel format for custom ones
      setCustomModels(custom.map(p => ({
        id: p.id,
        title: p.title,
        description: p.description,
        system_prompt: p.system_prompt,
        icon: 'Person',
      })));
    } catch (err) {
      console.error('Error fetching personalities:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Save all custom models to API
  const saveModels = useCallback(async (models: CustomModel[]) => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}/api/custom-models/save`, {
        method: 'POST',
        headers: {
          Authorization: `Auth ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ models }),
      });

      if (!response.ok) {
        throw new Error('Falha ao salvar modelos personalizados');
      }

      const data: CustomModelsResponse = await response.json();
      setCustomModels(data.models || []);
    } catch (err) {
      console.error('Error saving custom models:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Add a new model
  const addModel = useCallback(async (model: CustomModel) => {
    const newModels = [...customModels, model];
    await saveModels(newModels);
  }, [customModels, saveModels]);

  // Update an existing model
  const updateModel = useCallback(async (model: CustomModel) => {
    const newModels = customModels.map(m => m.id === model.id ? model : m);
    await saveModels(newModels);
  }, [customModels, saveModels]);

  // Delete a model
  const deleteModel = useCallback(async (modelId: string) => {
    const newModels = customModels.filter(m => m.id !== modelId);
    await saveModels(newModels);

    // If deleted model was active, clear it
    if (activeModelId === modelId) {
      setActiveModelId(null);
    }
  }, [customModels, saveModels, activeModelId, setActiveModelId]);

  // Fetch personalities on authentication
  useEffect(() => {
    if (isAuthenticated) {
      fetchPersonalities();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const value: CustomModelsContextValue = {
    customModels,
    predefinedPersonalities,
    loading,
    error,
    activeModelId,
    activeModel,
    fetchPersonalities,
    saveModels,
    addModel,
    updateModel,
    deleteModel,
    setActiveModelId,
    clearActiveModel,
    getActiveSystemPrompt,
    getActiveModelName,
  };

  return (
    <CustomModelsContext.Provider value={value}>
      {children}
    </CustomModelsContext.Provider>
  );
};

export default CustomModelsContext;
